import { ReactNode } from 'react'
import { Box, Stack, TableCell, Tooltip } from '@mui/material'
import { Check } from '@mui/icons-material'
import { com } from '@eidu/web'
import ActionIconIndicator from './ActionIconIndicator'
import {
  CRUD_ACTIONS,
  ColumnHeader,
  RowHeader,
  boldCheckSx,
  findEffectiveAccess,
  findEffectiveGlobalCheck,
  findEffectiveGlobalPolicyNames,
  findEffectivePolicyNames,
  formatPermissionTarget,
} from './utils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityWithLabelAndRelated from '../../../domain/entity/EntityWithLabelAndRelated'
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import Permission = com.eidu.sharedlib.entity.permission.Permission

type VisualizerTableCellProps = {
  row: Extract<RowHeader, { type: 'global' | 'entity' }>
  column: ColumnHeader
  columnByKey: ReadonlyMap<string, ColumnHeader>
  entityTypes: readonly EntityType[]
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
}

const buildTooltip = (
  permissions: readonly Permission[] | undefined,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>,
  policyNames: readonly string[]
): ReactNode => {
  const labels =
    permissions && permissions.length > 0
      ? Array.from(
          new Set(
            permissions.map((p) => formatPermissionTarget(p, entityTypes, entitiesByTypeId)).filter((s) => s.length > 0)
          )
        )
      : []
  if (labels.length === 0 && policyNames.length === 0) return null
  const policyLine =
    policyNames.length > 0 ? `Granted by polic${policyNames.length > 1 ? 'ies' : 'y'}: ${policyNames.join(', ')}` : null
  return (
    <Stack spacing={0.25}>
      {labels.map((label) => (
        <Box key={label} sx={{ whiteSpace: 'pre-wrap' }}>
          {label}
        </Box>
      ))}
      {policyLine ? <Box sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{policyLine}</Box> : null}
    </Stack>
  )
}

const VisualizerTableCell = ({ row, column, columnByKey, entityTypes, entitiesByTypeId }: VisualizerTableCellProps) => (
  <TableCell sx={{ borderRight: 1, borderColor: 'divider', textAlign: 'center' }}>
    {row.type === 'global' ? (
      row.action instanceof EntityAction ? (
        (() => {
          const result = findEffectiveAccess(columnByKey, column, row.action)
          const policyNames = findEffectivePolicyNames(columnByKey, column, row.action)
          const tooltip = buildTooltip(
            result?.sourceColumn.permissionsByAction.get(row.action),
            entityTypes,
            entitiesByTypeId,
            policyNames
          )
          return (
            <ActionIconIndicator
              Icon={Check}
              access={result?.access}
              iconSx={boldCheckSx}
              muted={result?.inherited}
              tooltip={tooltip}
              policyNames={policyNames}
            />
          )
        })()
      ) : (
        (() => {
          const result = findEffectiveGlobalCheck(columnByKey, column, row.action)
          const policyNames = findEffectiveGlobalPolicyNames(columnByKey, column, row.action)
          if (!result && policyNames.length === 0) return null
          const tooltip =
            policyNames.length > 0
              ? `Granted by polic${policyNames.length > 1 ? 'ies' : 'y'}: ${policyNames.join(', ')}`
              : undefined
          const icon = (
            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
              <Check
                fontSize="small"
                sx={[boldCheckSx, { color: result?.inherited ? 'text.disabled' : 'text.primary' }]}
              />
              {policyNames.length > 0 ? (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: -3,
                    top: -2,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                  }}
                />
              ) : null}
            </Box>
          )
          return tooltip ? (
            <Tooltip title={tooltip} arrow placement="top">
              {icon}
            </Tooltip>
          ) : (
            icon
          )
        })()
      )
    ) : row.type === 'entity' ? (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        {CRUD_ACTIONS.map(({ action, Icon }) => {
          const result = findEffectiveAccess(columnByKey, column, action, row.entityTypeId.toString())
          const policyNames = findEffectivePolicyNames(columnByKey, column, action, row.entityTypeId.toString())
          const tooltip = buildTooltip(
            result?.sourceColumn.permissionsByTypeAndAction.get(row.entityTypeId.toString())?.get(action),
            entityTypes,
            entitiesByTypeId,
            policyNames
          )
          return (
            <ActionIconIndicator
              key={action.toString()}
              Icon={Icon}
              access={result?.access}
              muted={result?.inherited}
              tooltip={tooltip}
              policyNames={policyNames}
            />
          )
        })}
      </Stack>
    ) : null}
  </TableCell>
)

export default VisualizerTableCell
